import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { submitInquiry } from "@/lib/api";
import { toast } from "sonner";

const serviceOptions = [
  { value: "consultancy", label: "Private Independent Consultancy" },
  { value: "decoding", label: "Behavioural Decoding Session" },
  { value: "routine-mapping", label: "Custom Household Routine-Mapping" },
  { value: "other", label: "I'm not sure yet" },
];

export default function Inquire() {
  const [params] = useSearchParams();
  const initialService = params.get("service") || "consultancy";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: initialService,
    children_ages: "",
    situation: "",
    consent: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.situation) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInquiry(form);
      setSent(true);
      toast.success("Inquiry received. I'll reply personally within 2 working days.");
    } catch (err) {
      toast.error("Could not send your inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <main data-testid="inquiry-success" className="container-edit pt-24 pb-32">
        <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-5">
          Received
        </p>
        <h1 className="font-serif text-5xl md:text-6xl text-brand-forest leading-tight max-w-3xl">
          Thank you. Your note has landed safely.
        </h1>
        <p className="mt-8 text-brand-charcoal leading-relaxed max-w-2xl">
          I read every inquiry personally and reply within two working days, often
          sooner. Replies come from a discreet email address — please check your
          spam folder once, then add me to your contacts.
        </p>
        <p className="mt-6 text-brand-muted text-sm">
          If this is an emergency or you are concerned for a child's immediate
          safety, please contact your local emergency services. UK readers can
          also call the NSPCC on 0808 800 5000.
        </p>
      </main>
    );
  }

  return (
    <main data-testid="inquiry-page" className="container-edit pt-16 md:pt-24 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-5">
            Private Inquiry
          </p>
          <h1 className="font-serif text-5xl md:text-6xl text-brand-forest leading-[0.98] tracking-tight">
            Tell me a little.<br />I'll reply personally.
          </h1>
          <p className="mt-8 text-brand-charcoal leading-relaxed">
            Everything here is read by me alone. Nothing you write is shared,
            stored on a CRM, or used to market to you. If 1:1 work isn't the right
            fit, I'll say so and point you somewhere it is.
          </p>
          <div className="mt-12 border-t border-brand-border pt-8 space-y-4 text-sm text-brand-muted leading-relaxed">
            <p><strong className="text-brand-forest">Reply time:</strong> within 2 working days.</p>
            <p><strong className="text-brand-forest">Confidentiality:</strong> independent of any court-appointed framework.</p>
            <p><strong className="text-brand-forest">In an emergency:</strong> always call your local emergency services first.</p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-brand-surface border border-brand-border p-8 md:p-12 space-y-8"
          data-testid="inquiry-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Field label="Your name *" required>
              <input
                required
                data-testid="inquiry-name"
                value={form.name}
                onChange={setField("name")}
                className="input-edit"
                autoComplete="name"
              />
            </Field>
            <Field label="Email *" required>
              <input
                required
                type="email"
                data-testid="inquiry-email"
                value={form.email}
                onChange={setField("email")}
                className="input-edit"
                autoComplete="email"
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                data-testid="inquiry-phone"
                value={form.phone}
                onChange={setField("phone")}
                className="input-edit"
                autoComplete="tel"
              />
            </Field>
            <Field label="Children's ages (optional)">
              <input
                data-testid="inquiry-ages"
                value={form.children_ages}
                onChange={setField("children_ages")}
                placeholder="e.g. 4 and 8"
                className="input-edit"
              />
            </Field>
          </div>

          <Field label="Which service interests you? *" required>
            <select
              data-testid="inquiry-service"
              value={form.service_type}
              onChange={setField("service_type")}
              className="input-edit"
            >
              {serviceOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Tell me a little about your situation *" required>
            <textarea
              required
              rows={6}
              data-testid="inquiry-situation"
              value={form.situation}
              onChange={setField("situation")}
              placeholder="As much or as little as you wish. The contact pattern, what you are seeing on return, what you most want help with."
              className="input-edit resize-none"
            />
          </Field>

          <p className="text-xs text-brand-muted leading-relaxed">
            By sending this form you consent to me holding this message
            confidentially for the sole purpose of replying to you. You may ask
            for it to be deleted at any time.
          </p>

          <button
            type="submit"
            disabled={submitting}
            data-testid="inquiry-submit"
            className="inline-flex items-center px-10 py-4 bg-brand-forest text-brand-bg text-sm tracking-wide hover:bg-brand-moss transition-colors rounded-sm disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send privately"}
          </button>
        </form>
      </div>

      <style>{`
        .input-edit {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1.5px solid #E5E0D8;
          padding: 0.65rem 0;
          font-size: 1rem;
          color: #1A2F25;
          outline: none;
          border-radius: 0;
          transition: border-color 0.3s ease;
        }
        .input-edit:focus {
          border-bottom-color: #1A2F25;
        }
        .input-edit::placeholder { color: #5C6B64; opacity: 0.7; }
      `}</style>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.25em] text-brand-muted mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
